import SearchBar from "@design-system/Inputs/SearchBar";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { keys, RESOURCE_NAME } from "../../i18n";
import { type ExternalQuestionnaireOptions } from "../../types";
import { EmptyQuestionnairePlaceholder } from "../EmptyQuestionnairePlaceholder/EmptyQuestionnairePlaceholder";
import { QuestionActions } from "../FilterChips/QuestionActions";
import { QuestionsTable } from "./components/QuestionsTable";
import { QuestionsTableFilters } from "./components/QuestionsTable/Filters";
import { type GetQuestionnaireOverviewDataQuery } from "@generated/client/graphql";

export const OverviewTab: FC<{
    questionnaireId: string | undefined;
    options: ExternalQuestionnaireOptions;
    questions: GetQuestionnaireOverviewDataQuery["getQuestionnaireOverviewData"]["questions"];
    isQuestionnaireGenerating: boolean;
    isQuestionsPending: boolean;
}> = ({
    questionnaireId,
    options,
    questions,
    isQuestionnaireGenerating,
    isQuestionsPending,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const [searchString, setSearchString] = useState<string | undefined>(
        undefined,
    );

    const isEmptyQuestionnaire = !questions?.length && !isQuestionsPending;

    const shouldShowPlaceholder =
        isEmptyQuestionnaire && !searchString && !isQuestionnaireGenerating;

    return (
        <FlexCol w="full" gap="4">
            {shouldShowPlaceholder ? (
                <FlexCol gap="3">
                    <FlexRow justifyContent="end">
                        <QuestionActions
                            questionnaireId={questionnaireId}
                            options={options}
                            questions={questions}
                            isQuestionnaireEmpty
                        />
                    </FlexRow>

                    <EmptyQuestionnairePlaceholder
                        isGeneratingQuestions={false}
                    />
                </FlexCol>
            ) : (
                <>
                    <FlexCol gap="3">
                        <QuestionsTableFilters />
                        <FlexRow justifyContent="between" alignItems="center">
                            <SearchBar
                                placeholder={t(keys.search_bar_placeholder)}
                                searchString={searchString}
                                setSearchString={setSearchString}
                                className="w-[250px]"
                            />

                            <QuestionActions
                                questionnaireId={questionnaireId}
                                options={options}
                                questions={questions}
                            />
                        </FlexRow>
                    </FlexCol>
                    <QuestionsTable
                        options={options}
                        questionnaireId={questionnaireId}
                        searchString={searchString}
                        showGroups={false}
                        loading={isQuestionsPending}
                        questions={questions ?? []}
                    />
                </>
            )}
        </FlexCol>
    );
};
